import { useCallback, useMemo } from "react";
import {
  matchPath,
  BlockerFunction,
  useLocation,
  useBlocker,
} from "react-router-dom";

interface Props {
  hasUnsavedChanges: boolean;
  handleSaveAsNew: (openNewWorkspace?: boolean) => Promise<void>;
  handleSave: () => Promise<void>;
  isSaving: boolean;
  userCanSave: boolean;
  discardChanges: () => void;
}

export const useWorkspaceNavigationBlocker = ({
  hasUnsavedChanges,
  handleSaveAsNew,
  handleSave,
  isSaving,
  userCanSave,
  discardChanges,
}: Props) => {
  const location = useLocation();

  const currentWorkspaceId = useMemo(() => {
    const match =
      matchPath("/projects/:workspaceId/*", location.pathname) ||
      matchPath("/workspaces/:workspaceId/*", location.pathname);
    return match?.params.workspaceId;
  }, [location.pathname]);

  // Conditional blocker function
  const shouldBlockNavigation: BlockerFunction = useCallback(
    ({ nextLocation }): boolean => {
      const nextMatch =
        matchPath("/projects/:workspaceId/*", nextLocation.pathname) ||
        matchPath("/workspaces/:workspaceId/*", nextLocation.pathname);
      const nextWorkspaceId = nextMatch?.params.workspaceId;
      return (
        hasUnsavedChanges && !isSaving && nextWorkspaceId !== currentWorkspaceId
      );
    },
    [hasUnsavedChanges, isSaving, currentWorkspaceId]
  );

  // Use blocker
  const blocker = useBlocker(shouldBlockNavigation);

  // Extract current workspace ID from path

  const onClose = useCallback(() => {
    blocker?.reset?.();
  }, [blocker]);

  const onSave = useCallback(async () => {
    if (userCanSave) {
      await handleSave();
    } else {
      //since we want to stay on the same page openNewWorkspace is false
      await handleSaveAsNew(false);
    }
    blocker?.proceed?.();
  }, [handleSaveAsNew, handleSave, blocker, userCanSave]);

  const onCancel = useCallback(() => {
    discardChanges();
    blocker?.proceed?.();
  }, [blocker, discardChanges]);

  return {
    isBlocked: blocker.state === "blocked",
    onSave,
    onClose,
    onCancel,
  };
};
